import { Injectable } from "@angular/core";
import { PromotionRepositoryInterface } from "./promotion.interface";
import { RemoteRepositoryInterface } from "../../remote/remote.interface";
import { concatMap, map } from "rxjs/operators";
import { Observable } from "rxjs";
import { PromotionList } from "../../entities/promotionlist.model";
import { PromotionModel } from "../../entities/promotion.model";
import { PromotionType } from "../../../crossconcern/types/promotion.type";
import { ProductUUIDType } from "../../../crossconcern/types/productuuid.type";
import { NurseryUUIDType } from "app/crossconcern/types/nurseryuuid";
import { MediaList } from "../../entities/medialist.model";
import { PROMOTION_IMAGE_TAG } from "../../../crossconcern/utilities/properties/base.property";

@Injectable()
export class PromotionRepository implements PromotionRepositoryInterface {

    constructor(private remoteRepository: RemoteRepositoryInterface) {}

    public getPromotions(pageSize: number, page: number, nurseryUuid: string, featured: boolean): Observable<PromotionList> {
        let url = "promotions?withProducts=true&withNurseries=true";
        if (nurseryUuid != null) {
            url += "&nurseryUuid=" + nurseryUuid;
        }
        if (featured != null) {
            url += "&search=featured:" + featured;
        }
        if(pageSize != null && page != null) {
            url += "&size=" + pageSize + "&page=" + page;
        }
        return this.remoteRepository.get(url).pipe(
            map((response) => new PromotionList(response as JSON))
        );
    }

    public getPromotion(uuid: string): Observable<PromotionModel> {
        return this.remoteRepository.get("promotions/" + uuid + "?withProducts=true&withNurseries=true").pipe(
            map((response) => new PromotionModel(response as JSON))
        );
    }

    public getPromotionMedia(uuid: string): Observable<MediaList> {
        const url = "media/entities/" + uuid
            + "?search=custom_tag:" + PROMOTION_IMAGE_TAG;
        return this.remoteRepository.get(url).pipe(
            map((response) => new MediaList(response as JSON))
        );
    }

    public addPromotion(promotionType: PromotionType, productUUIDType: ProductUUIDType,
        nurseryUUIDType: NurseryUUIDType): Observable<PromotionModel> {
        return this.remoteRepository.post("promotions", promotionType).pipe(
            concatMap((response) => {
                const promotion = new PromotionModel(response as JSON);
                return this.addPromotionProducts(promotion.uuid, productUUIDType)
                    .pipe(
                        concatMap(() => {
                            return this.addPromotionNurseries(promotion.uuid, nurseryUUIDType);
                        }),
                        map(() => promotion)
                    );
            })
        );
    }

    public addPromotionImage(body: FormData, promotionUUID: string): Observable<{}> {
        return this.remoteRepository.post("promotions/" + promotionUUID + "/image", body);
    }

    public deletePromotion(uuid: string): Observable<{}> {
        return this.remoteRepository.delete("promotions/" + uuid);
    }

    public updatePromotion(uuid: string, promotionType: PromotionType): Observable<PromotionModel> {
        return this.remoteRepository.update("promotions/" + uuid, promotionType)
            .pipe(
                map((resp) => new PromotionModel(resp as JSON))
            );
    }

    public addPromotionProducts(uuid: string, productUUIDType: ProductUUIDType): Observable<{}> {
        return this.remoteRepository.update("promotions/" + uuid + "/products", productUUIDType);
    }

    public deletePromotionProducts(uuid: string, productUUIDType: ProductUUIDType): Observable<{}> {
        return this.remoteRepository.deleteWithBody("promotions/" + uuid + "/products", productUUIDType);
    }

    public addPromotionNurseries(uuid: string, nurseryUUIDType: NurseryUUIDType): Observable<{}> {
        return this.remoteRepository.update("promotions/" + uuid + "/nurseries", nurseryUUIDType);
    }

    public deletePromotionNurseries(uuid: string, nurseryUUIDType: NurseryUUIDType): Observable<{}> {
        return this.remoteRepository.deleteWithBody("promotions/" + uuid + "/nurseries", nurseryUUIDType);
    }
}